import { Component, OnDestroy } from '@angular/core';
import { GuardsCheckEnd, Router } from '@angular/router';
import { RuntimeConfigurationService } from 'gain-web/shared-modules/runtime-configuration/runtime-configuration.service';
import { UserContextService } from 'gain-web/shared-modules/user/user-context.service';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { orderBy } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ClientContextService } from '../client-context.service';

interface Link {
  name: string;
  path: string;
  disabled: boolean;
}

@Component({
  selector: 'app-client-content',
  templateUrl: './client-content.component.html',
  styleUrls: ['./client-content.component.scss'],
  standalone: false,
})
export class ClientContentComponent implements OnDestroy {
  clientRootPath: string;
  clientName: string;
  clientId: string;
  links$: BehaviorSubject<Link[]> = new BehaviorSubject<Link[]>([]);
  activeLink$ = new BehaviorSubject<Link | undefined>(undefined);
  viewClientDashboard: boolean = false;
  viewChangeRequest: boolean = false;
  showChangeRequestTab: boolean = false;
  isClientUser: boolean = false;
  viewDataExchange: boolean = false;
  private _onDestroy$ = new Subject();
  get activeLink() {
    return orderBy(this.links$.getValue(), (l) => l.path.length, 'desc').find(
      (l) => this._router.url.toLowerCase().includes(l.path),
    );
  }

  get hideChangeRequests(): boolean {
    return !(this.viewChangeRequest ?? false);
  }

  get userConfigComplete(): boolean {
    return this.clientContext.client.state === ApiClient.ClientStateDto.Active;
  }

  get clientContext() {
    return this._clientContextService.value;
  }

  constructor(
    private runtimeConfigService: RuntimeConfigurationService,
    private _router: Router,
    private _clientContextService: ClientContextService,
    private _userContextService: UserContextService,
  ) {
    this.clientName = this._clientContextService.value.client.name;
    this.clientId = this._clientContextService.value.client.portalId;
    this.clientRootPath = `/clients/${this._clientContextService.value.client.portalId}`;
    this.viewClientDashboard =
      this._clientContextService.value.userOperations.viewClientDashboard ??
      false;
    this.viewChangeRequest =
      this._clientContextService.value.userOperations.viewChangeRequest ??
      false;
    this.showChangeRequestTab =
      this._clientContextService.value.userOperations.showChangeRequest ??
      false;
    this.viewDataExchange =
      this._clientContextService.value.userOperations.viewDataExchange ?? false;
    this.isClientUser =
      this._userContextService.value?.data.isClientUser ?? false;

    this.setLinks(this._clientContextService.value);
    this._clientContextService.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((clientContext) => this.setLinks(clientContext));

    this.activeLink$.next(this.activeLink);

    this._router.events
      .pipe(
        filter((e) => e instanceof GuardsCheckEnd),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => {
        this.activeLink$.next(this.activeLink);
      });
  }

  ngOnDestroy() {
    this._onDestroy$.next(undefined);
    this._onDestroy$.complete();
  }

  private setLinks(clientContext: ApiClient.ClientContextDto) {
    const topNavLinks = [
      {
        name: 'Configuration',
        path: `${this.clientRootPath}/configuration`,
        disabled: false,
      },
      {
        name: 'Calculations',
        path: `${this.clientRootPath}/calculations`,
        disabled:
          !clientContext.calculationsAreEnabled || !this.userConfigComplete,
      },
      {
        name: 'Reporting',
        path: `${this.clientRootPath}/reports`,
        disabled: !this.userConfigComplete,
      },
      {
        name: 'Overrides',
        path: `${this.clientRootPath}/overrides`,
        disabled: !this.userConfigComplete,
      },
      {
        name: 'Data Exchange',
        path: `${this.clientRootPath}/data-exchange`,
        disabled: false,
      },
      {
        name: 'Admin',
        path: `${this.clientRootPath}/admin`,
        disabled: !this.userConfigComplete,
      },
    ];

    // Add Client Dashboard tab left of Configuration tab
    if (this.viewClientDashboard) {
      const indexConfig = topNavLinks
        .map((e) => e.name)
        .indexOf('Configuration');
      if (indexConfig > -1) {
        const clientTab = {
          name: `${this.clientName}${!this.isClientUser ? ' - ' + this.clientId : ''}`,
          path: `${this.clientRootPath}/client`,
          disabled: false,
        };
        topNavLinks.splice(indexConfig, 0, clientTab);
      }
    }

    // Remove Data Exchange tab for client users
    if (!this.viewDataExchange) {
      const indexDE = topNavLinks.map((e) => e.name).indexOf('Data Exchange');
      if (indexDE > -1) {
        topNavLinks.splice(indexDE, 1);
      }
    }

    // Insert Change Request tab right after Data Exchange tab
    const indexNumber = topNavLinks.map((e) => e.name).indexOf('Data Exchange');
    if (this.showChangeRequestTab) {
      const changeRequest = {
        name: 'Change Request',
        path: `${this.clientRootPath}/change-request`,
        disabled: !this.userConfigComplete,
      };
      topNavLinks.splice(indexNumber + 1, 0, changeRequest);
    }

    this.links$.next(topNavLinks);
  }
}

